import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...args) {
  return twMerge(clsx(args));
}

// Security hardening (2026-09-07, XSS/DOM-sink audit, item 70): any
// user-editable freeform text field (e.g. a tenant's "website" branding
// field, set by a Company Admin) that later renders as an <a href={...}>
// in a HIGHER-privilege context (e.g. Platform Admin's CustomerDetail
// page) is a classic javascript:-URI stored-XSS vector -- React does
// NOT sanitize href schemes on your behalf. Only ever allow http(s); an
// unsafe/malformed value degrades to plain text, never an executable link.
export function safeExternalHref(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const parsed = new URL(url.trim(), window.location.origin);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.href;
    }
  } catch {
    // malformed URL -- fall through to null
  }
  return null;
}

export const PIN_STATUS = {
  open: { label: "Open", cls: "st-open" },
  assigned: { label: "Assigned", cls: "st-assigned" },
  escalated: { label: "Escalated", cls: "st-escalated" },
  in_progress: { label: "In Progress", cls: "st-in_progress" },
  pending_verification: { label: "Pending Verification", cls: "st-pending_verification" },
  corrected: { label: "Corrected", cls: "st-corrected" },
  closed: { label: "Closed", cls: "st-closed" },
  archived: { label: "Archived", cls: "st-archived" },
};

export const PIN_PRIORITY = {
  low: { label: "Low", cls: "pr-low" },
  medium: { label: "Medium", cls: "pr-medium" },
  high: { label: "High", cls: "pr-high" },
  critical: { label: "Critical", cls: "pr-critical" },
};

export const PIN_CATEGORY = {
  field_issue: { label: "Field Issue", color: "#fbbf24", icon: "AlertTriangle" },
  safety_concern: { label: "Safety Concern", color: "#ef4444", icon: "ShieldAlert" },
  rfi_needed: { label: "RFI Needed", color: "#60a5fa", icon: "HelpCircle" },
  inspection_item: { label: "Inspection Item", color: "#a78bfa", icon: "ClipboardCheck" },
  equipment_access: { label: "Equipment Access", color: "#fb923c", icon: "Truck" },
  utility_conflict: { label: "Utility Conflict", color: "#f59e0b", icon: "Zap" },
  survey_layout: { label: "Survey / Layout", color: "#34d399", icon: "Compass" },
  corrective_action: { label: "Corrective Action", color: "#F59E0B", icon: "Wrench" },
  photo_note: { label: "Photo Note", color: "#a8a29e", icon: "Camera" },
  super_note: { label: "Super Note", color: "#e85d1f", icon: "MessageSquare" },
};

export const WORKFLOW_TYPES = {
  rfi: { label: "RFI", color: "#60a5fa" },
  corrective_action: { label: "Corrective Action", color: "#F59E0B" },
  safety: { label: "Safety Issue", color: "#ef4444" },
  inspection: { label: "Inspection", color: "#a78bfa" },
  daily_report: { label: "Daily Report", color: "#34d399" },
  equipment: { label: "Equipment Issue", color: "#fb923c" },
  dispatch: { label: "Dispatch Need", color: "#f59e0b" },
  photo_report: { label: "Photo Report", color: "#a8a29e" },
  pm_review: { label: "PM Review", color: "#e85d1f" },
};

export const DISCIPLINE_LABEL = {
  general: "General",
  roadway: "Roadway",
  drainage: "Drainage",
  utility: "Utility",
  mot: "MOT",
  signing_striping: "Signing & Striping",
  structures: "Structures",
  electrical: "Electrical",
  survey: "Survey",
  landscaping: "Landscaping",
  airport_faa: "Airport / FAA",
  safety_phasing: "Safety / Phasing",
  paving: "Paving",
  grading: "Grading",
  concrete: "Concrete",
};

export const ROLE_LABEL = {
  owner: "Owner",
  executive: "Executive",
  admin: "Admin",
  project_manager: "Project Manager",
  superintendent: "Superintendent",
  field_engineer: "Field Engineer",
  safety_manager: "Safety Manager",
  inspector: "Inspector / QC",
  foreman: "Foreman",
  subcontractor: "Subcontractor",
  viewer: "Viewer",
};

export function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch { return "—"; }
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
}

export function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return formatDateShort(iso);
}

export function formatCurrency(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

export function isOverdue(iso, status) {
  if (!iso) return false;
  if (["closed", "archived", "corrected"].includes(status)) return false;
  return new Date(iso).getTime() < Date.now();
}
